import React, { forwardRef, useImperativeHandle, useState, useRef, CSSProperties } from 'react';

enum InputType {
    TEXT = 'text',
    NUMBER = 'number',
    TEL = 'tel',
    PASSWORD = 'password'
}

interface TextBoxProps {
    label: string;
    inputId: string;
    required?: boolean;
    errorMessage?: string;
    placeholder?: string;
    type?: InputType;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    maxLength?: number; // Yeni maxLength özelliği eklendi
    style?: CSSProperties | null; // style prop'u ekleniyor
}

export interface TextBoxHandle {
    validate: () => boolean;
    showError: () => void;
    hideError: () => void;
    getValue: () => string;
    setValue: (value: string) => void;
}

const TextBox = forwardRef<TextBoxHandle, TextBoxProps>(({
    label,
    inputId,
    required = false,
    errorMessage,
    placeholder,
    type = InputType.TEXT,
    onChange,
    value,
    maxLength, // maxLength özelliği kullanılıyor
    style = null // style prop'u varsayılan olarak null
}, ref) => {
    const [isValid, setIsValid] = useState(true);
    const [inputValue, setInputValue] = useState(value || '');
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
        validate: () => {
            const inputElement = inputRef.current;
            if (required && !inputElement?.value) {
                setIsValid(false);
                return false;
            }
            setIsValid(true);
            return true;
        },
        showError: () => {
            setIsValid(false);
        },
        hideError: () => {
            setIsValid(true);
        },
        getValue: () => {
            return inputRef.current?.value || '';
        },
        setValue: (value: string) => {
            setInputValue(value);
            if (inputRef.current) {
                inputRef.current.value = value;
            }
        }
    }));

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!maxLength || value.length <= maxLength) {
            setInputValue(value);
            if (onChange) {
                onChange(event);
            }
        }
    };

    return (
        <div className="form-group">
            <label className="form-label" htmlFor={inputId}>
                {label}
            </label>
            <input
                id={inputId}
                type={type}
                className={`form-control mb-0 ${!isValid ? 'is-invalid' : ''}`}
                required={required}
                placeholder={placeholder}
                onChange={handleChange}
                ref={inputRef}
                value={inputValue}
                maxLength={maxLength} // maxLength özelliği input'a atanıyor
                style={style ?? {}} // style prop'u null değilse uygulanıyor, yoksa boş stil kullanılıyor
            />
            {!isValid && (
                <div className="invalid-feedback">
                    {errorMessage}
                </div>
            )}
        </div>
    );
});

export { TextBox, InputType };
export default TextBox;
